import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    Tooltip,
    IconButton,
    CircularProgress
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { Line, Bar } from 'react-chartjs-2';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';
import DailySurveyChart from './DailySurveyChart/DailySurveyChart';
import { getUserProfile, getUserProfileById, getAllNotes } from '../../../services/userService';
import { fetchSurveyResponses } from '../../../services/surveyService';

const PreviewAndDownload = ({
    userId,
    performanceResponses,
    dailySurveyResponses,
    observationPeriods,
    selectedObservationPeriod,
    setSelectedObservationPeriod,
    isAdmin
}) => {
    const { t, i18n } = useTranslation();
    const [openDialog, setOpenDialog] = useState(false);
    const surveyChartRefs = useRef([]);
    const [chartData, setChartData] = useState(null);
    const [chronotype, setChronotype] = useState('');
    const [recommendedSleepTime, setRecommendedSleepTime] = useState('');
    const [recommendedWakeUpTime, setRecommendedWakeUpTime] = useState('');
    const [userFirstName, setUserFirstName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [q20Data, setQ20Data] = useState({ startTime: null, duration: null });
    const [adminNotes, setAdminNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [requestQueue, setRequestQueue] = useState([]);
    const [isProcessingQueue, setIsProcessingQueue] = useState(false);
    const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

    useEffect(() => {
        const fetchUserProfile = async () => {
            // Don't make API calls if userId is not provided or invalid (when in admin mode)
            if (isAdmin && (!userId || userId === 'undefined')) {
                console.warn('PreviewAndDownload: userId is not defined in admin mode, skipping data fetch');
                return;
            }

            try {
                let userProfile;
                if (isAdmin) {
                    userProfile = await getUserProfileById(userId);
                    // getUserProfileById returns { user: {...} }, need to extract user data
                    userProfile = userProfile.user || userProfile;
                } else {
                    userProfile = await getUserProfile();
                }
                setChronotype(userProfile.chronotype || 'Not specified');
                setRecommendedSleepTime(userProfile.recommendedSleepTime || 'Not specified');
                setRecommendedWakeUpTime(userProfile.recommendedWakeUpTime || 'Not specified');
                setUserFirstName(userProfile.firstname || '');
                setUserLastName(userProfile.lastname || '');

                // Fetch survey data for Q20 (working hours)
                try {
                    const surveyData = await fetchSurveyResponses(userId);
                    if (surveyData && surveyData.responses && surveyData.responses.q20) {
                        setQ20Data({
                            startTime: surveyData.responses.q20.startTime || null,
                            duration: surveyData.responses.q20.duration || null
                        });
                    }
                } catch (surveyError) {
                    console.error('Error fetching survey data:', surveyError);
                }

                // Fetch admin notes
                try {
                    const notes = await getAllNotes(userId);
                    setAdminNotes(notes || []);
                } catch (notesError) {
                    console.error('Error fetching admin notes:', notesError);
                    setAdminNotes([]);
                }
            } catch (error) {
                console.error('Error fetching user profile:', error);
            }
        };
        fetchUserProfile();
    }, [userId, isAdmin]);

    const processTotalScoresByWeekday = (data) => {
        const weekdayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        const scoresByWeekday = weekdayOrder.map(day => ({
            weekday: day,
            scores: []
        }));

        data.forEach(response => {
            const date = new Date(response.timestamp);
            const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
            const dayData = scoresByWeekday.find(d => d.weekday === weekday);
            if (dayData) {
                dayData.scores.push(response.score);
            }
        });

        // Calculate average scores for each weekday
        const averageScores = scoresByWeekday.map(d => {
            if (d.scores.length === 0) return 0;
            const sum = d.scores.reduce((acc, score) => acc + score, 0);
            return sum / d.scores.length;
        });

        const chartData = {
            labels: weekdayOrder,
            datasets: [{
                label: t('overview.aggregateScoresChart'),
                data: averageScores,
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1
            }]
        };

        return { chartData, scoresByWeekday };
    };

    const makeGeminiRequest = useCallback(async (prompt, retryCount = 0, maxRetries = 3) => {
        if (!GEMINI_API_KEY) {
            throw new Error('Gemini API key is not configured');
        }

        try {
            // Add delay between retries
            if (retryCount > 0) {
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
            }

            // Format request for Gemini API
            const requestBody = {
                contents: [{
                    parts: [{
                        text: prompt
                    }]
                }],
                generationConfig: {
                    temperature: 0.1,
                    topK: 40,
                    topP: 0.95,
                    maxOutputTokens: 2048,
                    thinkingConfig: { thinkingBudget: 0 },
                }
            };

            // Make the request to Gemini API
            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody)
                }
            );

            if (response.status === 429 || response.status === 503) {
                // Rate limit or transient overload - back off and retry
                const retryAfter = parseInt(response.headers.get('Retry-After') || '60', 10);

                if (retryCount < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
                    return makeGeminiRequest(prompt, retryCount + 1, maxRetries);
                } else {
                    const err = new Error(response.status === 503
                        ? 'Gemini service unavailable (503) after retries'
                        : 'Max retries reached due to rate limiting');
                    err.status = response.status;
                    throw err;
                }
            }

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Gemini API error: ${response.status} - ${errorData}`);
            }

            const data = await response.json();

            // Extract the response text from Gemini's response format
            if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts) {
                const responseText = data.candidates[0].content.parts[0].text;
                return responseText;
            } else {
                throw new Error('Invalid response format from Gemini API');
            }
        } catch (error) {
            console.error('Error calling Gemini API:', error);
            throw error;
        }
    }, [GEMINI_API_KEY]);

    // Process queue sequentially
    const processQueue = useCallback(async () => {
        if (isProcessingQueue || requestQueue.length === 0) {
            return;
        }

        setIsProcessingQueue(true);

        while (requestQueue.length > 0) {
            const request = requestQueue[0];
            try {
                const result = await makeGeminiRequest(request.prompt);
                request.resolve(result);
            } catch (error) {
                request.reject(error);
            }
            setRequestQueue(prev => prev.slice(1));
            // Add delay between requests to avoid rate limiting
            await new Promise(resolve => setTimeout(resolve, 2000));
        }

        setIsProcessingQueue(false);
    }, [requestQueue, isProcessingQueue, makeGeminiRequest]);

    // Start processing when queue changes
    useEffect(() => {
        processQueue();
    }, [requestQueue, processQueue]);

    const processScoresByTimeSlot = (data) => {
        // Find min and max hours from the actual data
        let minHour = 23;
        let maxHour = 0;

        data.forEach(response => {
            const hour = new Date(response.timestamp).getHours();
            minHour = Math.min(minHour, hour);
            maxHour = Math.max(maxHour, hour);
        });

        // If no data, default to 8-22 range
        if (minHour === 23 && maxHour === 0) {
            minHour = 8;
            maxHour = 22;
        }

        const scoresBySlot = {};
        const timeSlots = [];
        for (let i = minHour; i <= maxHour; i++) {
            const slot = `${i < 10 ? '0' : ''}${i}:00`;
            timeSlots.push(slot);
            scoresBySlot[slot] = [];
        }

        data.forEach(response => {
            const hour = new Date(response.timestamp).getHours();
            const timeSlot = `${hour < 10 ? '0' : ''}${hour}:00`;
            if (scoresBySlot[timeSlot] !== undefined) {
                scoresBySlot[timeSlot].push(response.score);
            }
        });

        const averageScores = timeSlots.map(slot => {
            const scores = scoresBySlot[slot];
            return scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
        });

        return {
            labels: timeSlots,
            datasets: [{
                label: t('overview.scoresByTimeSlotChart'),
                data: averageScores,
                backgroundColor: 'rgba(153, 102, 255, 0.6)',
                borderColor: 'rgba(153, 102, 255, 1)',
                borderWidth: 1
            }]
        };
    };

    const processDetailedPerformanceData = (responses, startDate, endDate) => {
        const filteredResponses = responses.filter(response => {
            const responseDate = new Date(response.timestamp);
            return responseDate >= startDate && responseDate <= endDate;
        });

        // Group by day
        const byDay = {};
        const byTimeSlot = {};

        filteredResponses.forEach(response => {
            const date = new Date(response.timestamp);
            const dayKey = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            const hour = date.getHours();
            const timeSlot = `${hour < 10 ? '0' : ''}${hour}:00`;

            // Group by day
            if (!byDay[dayKey]) {
                byDay[dayKey] = [];
            }
            byDay[dayKey].push({
                time: timeSlot,
                score: response.score
            });

            // Group by time slot
            if (!byTimeSlot[timeSlot]) {
                byTimeSlot[timeSlot] = [];
            }
            byTimeSlot[timeSlot].push({
                day: dayKey,
                score: response.score
            });
        });

        // Calculate total number of days in observation period
        const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

        // Format day-by-day breakdown
        const dayByDayText = Object.entries(byDay)
            .map(([day, entries]) => {
                const entryText = entries
                    .sort((a, b) => a.time.localeCompare(b.time))
                    .map(e => `${e.time}→${e.score}`)
                    .join(', ');
                return `${day}: ${entryText}`;
            })
            .join('\n');

        // Format time slot summary with frequency analysis
        const timeSlotSummary = Object.entries(byTimeSlot)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([timeSlot, entries]) => {
                const scores = entries.map(e => e.score);
                const average = (scores.reduce((sum, s) => sum + s, 0) / scores.length).toFixed(2);
                const count = entries.length;
                const frequency = ((count / totalDays) * 100).toFixed(0);
                const isSporadic = count <= 2 || frequency < 30;
                const sporadicFlag = isSporadic ? ' [SPORADIC]' : '';

                return `${timeSlot} - ${count} entries out of ${totalDays} days (${frequency}%)${sporadicFlag}\n  Values: [${scores.join(', ')}], Average: ${average}`;
            })
            .join('\n');

        return {
            dayByDay: dayByDayText,
            timeSlotSummary: timeSlotSummary,
            totalDays: totalDays,
            totalEntries: filteredResponses.length
        };
    };

    const processChartData = () => {
        if (!selectedObservationPeriod) return null;

        const startDate = new Date(selectedObservationPeriod.startDate);
        const endDate = new Date(selectedObservationPeriod.endDate);

        const filteredResponses = performanceResponses.filter(response => {
            const responseDate = new Date(response.timestamp);
            return responseDate >= startDate && responseDate <= endDate;
        });

        // Find min and max hours from the actual data
        let minHour = 23;
        let maxHour = 0;

        filteredResponses.forEach(response => {
            const hour = new Date(response.timestamp).getHours();
            minHour = Math.min(minHour, hour);
            maxHour = Math.max(maxHour, hour);
        });

        // If no data, default to 8-22 range
        if (minHour === 23 && maxHour === 0) {
            minHour = 8;
            maxHour = 22;
        }

        const scoresByHour = {};
        const hours = [];
        for (let i = minHour; i <= maxHour; i++) {
            const hourStr = `${i < 10 ? '0' : ''}${i}:00`;
            hours.push(hourStr);
            scoresByHour[hourStr] = [];
        }

        filteredResponses.forEach(response => {
            const date = new Date(response.timestamp);
            const hour = date.getHours();
            const hourStr = `${hour < 10 ? '0' : ''}${hour}:00`;
            if (scoresByHour[hourStr] !== undefined) {
                scoresByHour[hourStr].push(response.score);
            }
        });

        const averageScores = hours.map(hour => {
            const scores = scoresByHour[hour];
            return scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 0;
        });

        return {
            labels: hours,
            datasets: [{
                label: t('overview.performanceLineChart'),
                data: averageScores,
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                tension: 0.4
            }]
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: t('overview.performanceLineChart'),
                font: {
                    size: 16
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 10
            }
        }
    };

    // Function to check if we're still in the observation period
    const isCurrentlyInObservationPeriod = (period) => {
        if (!period) return false;
        const today = new Date();
        const endDate = new Date(period.endDate);
        return today <= endDate;
    };

    const handlePreview = async () => {
        if (!selectedObservationPeriod) {
            alert(t('alerts.selectObservationPeriod'));
            return;
        }

        // Filter responses for the selected period
        const startDate = new Date(selectedObservationPeriod.startDate);
        const endDate = new Date(selectedObservationPeriod.endDate);
        // Set end date to end of day to include all responses on the end date
        endDate.setHours(23, 59, 59, 999);

        const filteredPerformanceResponses = performanceResponses.filter(response => {
            const responseDate = new Date(response.timestamp);
            return responseDate >= startDate && responseDate <= endDate;
        });

        const filteredDailySurveyResponses = dailySurveyResponses.filter(response => {
            const responseDate = new Date(response.date);
            // Normalize to start of day for comparison
            responseDate.setHours(0, 0, 0, 0);
            const normalizedStartDate = new Date(startDate);
            normalizedStartDate.setHours(0, 0, 0, 0);
            const normalizedEndDate = new Date(endDate);
            normalizedEndDate.setHours(23, 59, 59, 999);

            const isInRange = responseDate >= normalizedStartDate && responseDate <= normalizedEndDate;

            return isInRange;
        });

        if (filteredPerformanceResponses.length === 0) {
            alert(t('alerts.noPerformanceData'));
            return;
        }

        setIsLoading(true);

        try {
            // Check if we're currently in the observation period
            const isGerman = i18n.language === 'de';
            const stillInObservationPeriod = isCurrentlyInObservationPeriod(selectedObservationPeriod);
            const observationPeriodStatus = stillInObservationPeriod
                ? (isGerman
                    ? "Der ausgewählte Beobachtungszeitraum umfasst das aktuelle Datum, was bedeutet, dass die Datenerfassung noch läuft. Dies kann die Genauigkeit der Analyse beeinflussen."
                    : "The selected observation period includes the current date, which means data collection is still ongoing. This may affect the accuracy of the analysis.")
                : (isGerman
                    ? "Der ausgewählte Beobachtungszeitraum ist abgeschlossen, sodass die Analyse auf dem vollständigen Datensatz basiert."
                    : "The selected observation period is complete, so the analysis is based on the full dataset.");

            // Generate the performance line chart data first using our improved method
            const performanceLineChart = processChartData();

            // Generate detailed data breakdown
            const detailedData = processDetailedPerformanceData(
                performanceResponses,
                startDate,
                endDate
            );

            // Generate data for other charts
            const { chartData: totalScoresChart, scoresByWeekday } = processTotalScoresByWeekday(filteredPerformanceResponses);
            const scoresByTimeSlotChart = processScoresByTimeSlot(filteredPerformanceResponses);

            // Generate the analysis prompts with modified format for Gemini API compatibility

            const performanceLineChartPrompt = isGerman ? `
                Analysieren Sie die folgenden Leistungsdaten in Verbindung mit dem Chronotyp des Nutzers "${chronotype}",
                empfohlener Aufwachzeit "${recommendedWakeUpTime}", empfohlener Schlafenszeit "${recommendedSleepTime}",
                ${q20Data.startTime ? `angegebene Arbeitsanfangszeit "${q20Data.startTime}" und Arbeitsdauer "${q20Data.duration} Stunden"` : 'keine angegebenen Arbeitszeiten'}.

                LEISTUNGSWERTE-INTERPRETATION:
                - Werte von 1-10: Tatsächliche Leistungsbewertungen (10 = höchste Produktivität, 1 = niedrigste)
                - Wert 0: KEINE EINGABE - bedeutet keine Daten für dieses Zeitfenster. Dies kann mehrere Ursachen haben:
                  * Arbeitspause oder Freizeit
                  * Außerhalb der Arbeitszeiten
                  * Vergessen, Leistung zu protokollieren (besonders bei unregelmäßigen/sporadischen Einträgen)

                BEOBACHTUNGSZEITRAUM: ${detailedData.totalDays} Tage (${new Date(startDate).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' })} - ${new Date(endDate).toLocaleDateString('de-DE', { month: 'short', day: 'numeric', year: 'numeric' })})
                GESAMTZAHL DER EINTRÄGE: ${detailedData.totalEntries}

                === TAG-FÜR-TAG-AUFSCHLÜSSELUNG ===
                ${detailedData.dayByDay}

                === ZEITFENSTER-ZUSAMMENFASSUNG (mit Häufigkeitsanalyse) ===
                ${detailedData.timeSlotSummary}

                WICHTIGER BEOBACHTUNGSZEITRAUM-STATUS: ${observationPeriodStatus}

                WICHTIG: Beginnen Sie direkt mit der Analyse. Keine Einleitung, Begrüßung oder abschließende Bemerkungen.

                DETAILLIERTE ANALYSE-ANWEISUNGEN:

                1. DATENEINGABEMUSTER IDENTIFIZIEREN:
                   - Analysieren Sie SORGFÄLTIG, welche Zeitfenster KONSISTENTE Eingaben haben (häufig >30% der Tage) vs. SPORADISCHE Eingaben (markiert mit [SPORADIC]) vs. KEINE Eingaben
                   - Identifizieren Sie das PRIMÄRE Arbeitszeitfenster (Zeitfenster mit den meisten konsistenten Einträgen)
                   - Beachten Sie sporadische Einträge außerhalb des primären Fensters (z.B. vereinzelte Nachmittags- oder Abendeinträge)

                2. VERGLEICH: ANGEGEBENE vs. TATSÄCHLICHE ARBEITSZEITEN:
                   ${q20Data.startTime ? `
                   - Angegebene Arbeitszeiten: Start um ${q20Data.startTime}, Dauer ${q20Data.duration} Stunden (erwartetes Ende: ca. ${parseInt(q20Data.startTime.split(':')[0]) + parseInt(q20Data.duration)}:00)
                   - Tatsächliche Dateneingaben: Analysieren Sie die Tag-für-Tag-Aufschlüsselung
                   - WICHTIGE DISKREPANZEN:
                     * Beginnt die Dateneingabe FRÜHER als angegeben? (z.B. Einträge ab 6 Uhr, aber angegeben 8 Uhr)
                     * Endet die Dateneingabe SPÄTER als angegeben?
                     * Gibt es eine LÜCKE zwischen angegebener Arbeitszeit und tatsächlichen Einträgen?
                   - Interpretieren Sie diese Diskrepanzen: Arbeitet die Person tatsächlich zu anderen Zeiten? Oder ist die Angabe ungenau? Oder wird vergessen zu protokollieren?
                   ` : `
                   - Keine Arbeitszeiten angegeben. Leiten Sie das wahrscheinliche Arbeitszeitfenster aus den Datenmustern ab.
                   `}

                3. CHRONOTYP-ÜBEREINSTIMMUNG:
                   - Für ${chronotype}:
                     * Morgentypen: Erwarten Sie höhere Leistung am Vormittag (6-12 Uhr), nachlassend am Nachmittag
                     * Abendtypen: Erwarten Sie niedrigere Leistung am frühen Morgen, steigend im Laufe des Tages
                     * Zwischentypen: Relativ gleichmäßige Leistung über den Tag
                   - Stimmt das Dateneingabemuster mit dem Chronotyp überein?
                   - Arbeitet die Person zu Zeiten, die NICHT ihrem Chronotyp entsprechen? Was sagt dies aus?

                4. LEISTUNGSMUSTER UND VARIABILITÄT:
                   - Konzentrieren Sie sich auf Zeitfenster mit KONSISTENTEN Werten
                   - Analysieren Sie die INDIVIDUELLEN WERTE (nicht nur Durchschnitte):
                     * Gibt es hohe VARIABILITÄT? (z.B. Werte von 3 bis 10 im selben Zeitfenster)
                     * Gibt es KONSISTENTE Muster? (z.B. immer 7-8 am Morgen)
                   - Identifizieren Sie Leistungsspitzen und -täler
                   - Beachten Sie Trends im Tagesverlauf

                5. INTERPRETATION VON LÜCKEN UND SPORADISCHEN EINTRÄGEN:
                   - NACHMITTAGSLÜCKEN: Wenn es konsistente Morgendaten gibt, aber nur sporadische/keine Nachmittagsdaten:
                     * Könnte bedeuten: Kürzere Arbeitszeiten, flexible Arbeitsplanung, oder Vergessen zu protokollieren
                     * Vergleichen Sie mit Chronotyp: Bei Morgentypen natürlicher; bei Abendtypen könnte auf Diskrepanz oder fehlende Protokollierung hinweisen
                   - SPORADISCHE SPÄTE EINTRÄGE (z.B. 21-22 Uhr):
                     * Analysieren Sie: Wie viele Male? An welchen Tagen? Könnte gelegentliche Überstunden, flexible Arbeitszeiten oder spezielle Projekte bedeuten
                   - WICHTIG: Fehlende Daten ≠ automatisch keine Arbeit! Könnte auch Vergessen bedeuten.

                6. TRENDANALYSE:
                   - Wo ENDEN typischerweise die Dateneingaben? (z.B. "Einträge enden typischerweise gegen 12 Uhr mittags")
                   - Gibt es ein klares Muster, WANN keine weiteren Eingaben mehr erfolgen?
                   - Vergleichen Sie dies mit angegebenen Arbeitszeiten und Chronotyp

                STIL-ANWEISUNGEN:
                - Schreiben Sie eine gründliche, aber kompakte Analyse. Halten Sie die gesamte Antwort unter ~600 Tokens und beenden Sie IMMER jeden Satz vollständig — niemals mitten im Gedanken abbrechen.
                - Verwenden Sie vollständige Sätze und zusammenhängende Absätze
                - Vermeiden Sie Aufzählungspunkte in der finalen Antwort
                - Verwenden Sie "Sie" statt "Nutzer"
                - Vermeiden Sie "Ich" und unsichere Formulierungen wie "vermuten" (verwenden Sie "könnte hinweisen auf", "lässt darauf schließen", "deutet darauf hin")
                - Seien Sie SPEZIFISCH mit Zeitangaben, Mustern und konkreten Beispielen aus den Daten

                ${stillInObservationPeriod ? "WICHTIG: Beginnen Sie mit einer kurzen Anmerkung, dass der Beobachtungszeitraum noch läuft und vollständigere Daten genauere Analysen ermöglichen würden. Fahren Sie dann fort." : ""}

                ${adminNotes.length > 0 ? `
                === ZUSÄTZLICHE ERKENNTNISSE VOM FORSCHUNGSTEAM ===
                Unser Forschungsteam hat die folgenden Beobachtungen und Erkenntnisse bereitgestellt, die bei der Analyse berücksichtigt werden sollten:

                ${adminNotes.map((note, index) => `
                Notiz ${index + 1} (vom ${new Date(note.createdAt).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' })}):
                ${note.comments}
                `).join('\n')}

                WICHTIG: Integrieren Sie diese Erkenntnisse des Forschungsteams in Ihre abschließende Analyse und Empfehlungen. Erwähnen Sie explizit, dass diese zusätzlichen Einsichten von unserem Forschungsteam stammen und zur Kontextualisierung der Datenmuster beitragen.
                ` : ''}

                EMPFEHLUNGEN UND FAZIT:
                Bieten Sie KONKRETE und UMSETZBARE Empfehlungen:
                - Optimale Zeiten für wichtige Aufgaben basierend auf dem Chronotyp UND den tatsächlichen Leistungsmustern
                - Wenn Diskrepanz zwischen Chronotyp und Arbeitszeiten: Vorschläge zur Anpassung (falls möglich) oder Ausgleichsstrategien
                - Wenn sporadische Dateneingabe: Empfehlung für konsistenteres Tracking zur besseren Analyse
                - Wenn früher Arbeitsbeginn trotz später Chronotyp: Ausgleichsstrategien (Pausen, wichtige Aufgaben später planen, Lichttherapie)
                - Strategien zur Leistungsoptimierung während der tatsächlichen Arbeitszeiten basierend auf den beobachteten Mustern
                ${adminNotes.length > 0 ? '- Berücksichtigen Sie die vom Forschungsteam bereitgestellten zusätzlichen Erkenntnisse in Ihren Empfehlungen' : ''}

                ${adminNotes.length > 0 ? 'Schließen Sie Ihre Analyse mit einem Fazit ab, das die Datenmuster mit den Erkenntnissen des Forschungsteams verbindet und einen ganzheitlichen Überblick bietet.' : ''}

                WICHTIG: Antworten Sie auf Deutsch.
            ` : `
                Analyze the following performance data in conjunction with the user's chronotype "${chronotype}",
                recommended wake-up time "${recommendedWakeUpTime}", recommended sleep time "${recommendedSleepTime}",
                ${q20Data.startTime ? `stated work start time "${q20Data.startTime}" and work duration "${q20Data.duration} hours"` : 'no stated working hours'}.

                PERFORMANCE VALUES INTERPRETATION:
                - Values 1-10: Actual performance ratings (10 = highest productivity, 1 = lowest)
                - Value 0: NO INPUT - means no data for this time slot. This can have multiple causes:
                  * Work break or leisure time
                  * Outside working hours
                  * Forgot to log performance (especially with irregular/sporadic entries)

                OBSERVATION PERIOD: ${detailedData.totalDays} days (${new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
                TOTAL ENTRIES: ${detailedData.totalEntries}

                === DAY-BY-DAY BREAKDOWN ===
                ${detailedData.dayByDay}

                === TIME SLOT SUMMARY (with Frequency Analysis) ===
                ${detailedData.timeSlotSummary}

                IMPORTANT OBSERVATION PERIOD STATUS: ${observationPeriodStatus}

                IMPORTANT: Start directly with the analysis. No introduction, greetings, or closing remarks.

                DETAILED ANALYSIS INSTRUCTIONS:

                1. IDENTIFY DATA ENTRY PATTERNS:
                   - Analyze CAREFULLY which time slots have CONSISTENT entries (frequently >30% of days) vs. SPORADIC entries (marked with [SPORADIC]) vs. NO entries
                   - Identify the PRIMARY working time window (time slots with the most consistent entries)
                   - Note sporadic entries outside the primary window (e.g., isolated afternoon or evening entries)

                2. COMPARISON: STATED vs. ACTUAL WORKING HOURS:
                   ${q20Data.startTime ? `
                   - Stated working hours: Start at ${q20Data.startTime}, duration ${q20Data.duration} hours (expected end: approx. ${parseInt(q20Data.startTime.split(':')[0]) + parseInt(q20Data.duration)}:00)
                   - Actual data entries: Analyze the day-by-day breakdown
                   - IMPORTANT DISCREPANCIES:
                     * Does data entry START EARLIER than stated? (e.g., entries from 6am but stated 8am)
                     * Does data entry END LATER than stated?
                     * Is there a GAP between stated working hours and actual entries?
                   - Interpret these discrepancies: Does the person actually work at different times? Or is the stated time inaccurate? Or are they forgetting to log?
                   ` : `
                   - No working hours stated. Derive the likely working time window from data patterns.
                   `}

                3. CHRONOTYPE ALIGNMENT:
                   - For ${chronotype}:
                     * Morning types: Expect higher performance in morning (6am-12pm), declining in afternoon
                     * Evening types: Expect lower performance in early morning, increasing throughout day
                     * Intermediate types: Relatively even performance across the day
                   - Does the data entry pattern align with the chronotype?
                   - Is the person working at times that DON'T suit their chronotype? What does this suggest?

                4. PERFORMANCE PATTERNS AND VARIABILITY:
                   - Focus on time slots with CONSISTENT values
                   - Analyze the INDIVIDUAL VALUES (not just averages):
                     * Is there high VARIABILITY? (e.g., values from 3 to 10 in the same time slot)
                     * Are there CONSISTENT patterns? (e.g., always 7-8 in the morning)
                   - Identify performance peaks and valleys
                   - Note trends throughout the day

                5. INTERPRETING GAPS AND SPORADIC ENTRIES:
                   - AFTERNOON GAPS: If there's consistent morning data but only sporadic/no afternoon data:
                     * Could mean: Shorter working hours, flexible schedule, or forgetting to log
                     * Compare with chronotype: For morning types natural; for evening types could indicate discrepancy or missing logging
                   - SPORADIC LATE ENTRIES (e.g., 9-10pm):
                     * Analyze: How many times? Which days? Could mean occasional overtime, flexible hours, or special projects
                   - IMPORTANT: Missing data ≠ automatically no work! Could also mean forgetting to log.

                6. TREND ANALYSIS:
                   - Where do data entries typically END? (e.g., "entries typically end around 12pm noon")
                   - Is there a clear pattern of WHEN no more entries occur?
                   - Compare this with stated working hours and chronotype

                STYLE INSTRUCTIONS:
                - Write a thorough but compact analysis. Keep the entire response under ~600 tokens and ALWAYS finish every sentence — do not stop mid-thought.
                - Use complete sentences and coherent paragraphs
                - Avoid bullet points in the final response
                - Use "You" instead of "user"
                - Avoid "I" and uncertain phrases like "suppose" (use "could indicate", "suggests that", "points to")
                - Be SPECIFIC with times, patterns, and concrete examples from the data

                ${stillInObservationPeriod ? "IMPORTANT: Begin with a brief note that the observation period is still ongoing and more complete data would enable more accurate analysis. Then proceed." : ""}

                ${adminNotes.length > 0 ? `
                === ADDITIONAL INSIGHTS FROM RESEARCH TEAM ===
                Our research team has provided the following observations and insights that should be considered in the analysis:

                ${adminNotes.map((note, index) => `
                Note ${index + 1} (from ${new Date(note.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}):
                ${note.comments}
                `).join('\n')}

                IMPORTANT: Integrate these insights from the research team into your final analysis and recommendations. Explicitly mention that these additional insights come from our research team and contribute to contextualizing the data patterns.
                ` : ''}

                RECOMMENDATIONS AND CONCLUSION:
                Offer CONCRETE and ACTIONABLE recommendations:
                - Optimal times for important tasks based on chronotype AND actual performance patterns observed
                - If discrepancy between chronotype and working hours: Suggestions for adjustment (if possible) or compensation strategies
                - If sporadic data entry: Recommendation for more consistent tracking to enable better analysis
                - If early work start despite late chronotype: Compensation strategies (breaks, schedule important tasks later, light therapy)
                - Strategies to optimize performance during actual working hours based on observed patterns
                ${adminNotes.length > 0 ? '- Incorporate the additional insights provided by the research team into your recommendations' : ''}

                ${adminNotes.length > 0 ? 'Conclude your analysis with a summary that connects the data patterns with the research team insights and provides a holistic overview.' : ''}
            `;

            const wakeBedQualityPrompt = isGerman ? `
                Analysieren Sie die kombinierten Erkenntnisse aus dem Aufwachzeit-Diagramm, Schlafenszeit-Diagramm und Schlafqualitäts-Diagramm in Verbindung mit dem Chronotyp des Nutzers "${chronotype}",
                empfohlener Aufwachzeit "${recommendedWakeUpTime}" und empfohlener Schlafenszeit "${recommendedSleepTime}".
    
                WICHTIGER BEOBACHTUNGSZEITRAUM-STATUS: ${observationPeriodStatus}
    
                WICHTIG: Beginnen Sie direkt mit der Analyse. Keine Einleitung wie "Okay, lass uns analysieren" oder "Hier ist meine Analyse". Keine Begrüßung oder abschließende Bemerkungen.
                
                Verwenden Sie nicht "Nutzer" in der Antwort, verwenden Sie stattdessen "Sie". Verwenden Sie nicht "vermuten"; wenn ein Wert 0 enthält, ist dieses Feld ohne Nutzereingabe leer.
                Es ist nicht notwendig, alle Einzelwerte aufzulisten, konzentrieren Sie sich auf Muster oder signifikante Werte. Schreiben Sie in aufeinanderfolgenden Sätzen und Absätzen, keine Aufzählungspunkte.
                Verwenden Sie nicht "Ich".
                
                ${stillInObservationPeriod ? "Erkennen Sie zu Beginn Ihrer Antwort an, dass der Beobachtungszeitraum noch läuft und dass vollständigere Daten eine genauere Analyse ermöglichen würden. Fahren Sie dann mit Ihrer Analyse basierend auf den verfügbaren Daten fort." : ""}
        
                Geben Sie eine Zusammenfassung, wie diese Faktoren mit optimaler Produktivität übereinstimmen und umsetzbare Vorschläge zur Verbesserung von Leistung und Erholung.
                Stellen Sie sicher, dass die Antwort prägnant ist und etwa 500 Token umfasst, beenden Sie die Sätze.
                WICHTIG: Antworten Sie auf Deutsch.
            ` : `
                Analyze the combined insights from the wake-up time chart, bedtime chart, and sleep quality chart in conjunction with the user's chronotype "${chronotype}", 
                recommended wake-up time "${recommendedWakeUpTime}", and recommended sleep time "${recommendedSleepTime}".
    
                IMPORTANT OBSERVATION PERIOD STATUS: ${observationPeriodStatus}
    
                IMPORTANT: Start directly with the analysis. No introduction like "Okay, let's analyze" or "Here is my analysis". No greetings or closing remarks.
                
                Do not use "user" in response, use "You" instead. Do not use "suppose"; if a value contains 0, that field is empty without the user's input. 
                It is not necessary to list all the single values, just focus on pattern or significant values. write in consecutive sentences and paragraphs, no bullet points.
                Do not use "I".
                
                ${stillInObservationPeriod ? "At the beginning of your response, acknowledge that the observation period is still ongoing and that more complete data would provide a more accurate analysis. Then proceed with your analysis based on the available data." : ""}
        
                Provide a summary of how these factors align with optimal productivity and actionable suggestions to improve performance and rest. 
                Ensure the response is concise and around 500 tokens, finish the sentences.
            `;

            const totalScoresTimeSlotPrompt = isGerman ? `
                Analysieren Sie die folgenden Leistungsdaten in Verbindung mit dem Chronotyp des Nutzers "${chronotype}",
                empfohlener Aufwachzeit "${recommendedWakeUpTime}" und empfohlener Schlafenszeit "${recommendedSleepTime}":
    
                Hier sind die Leistungswerte für jeden Wochentag:
                ${scoresByWeekday
                    .map(
                        ({ weekday, scores }) =>
                            `${weekday}: ${scores.length > 0 ? scores.join(', ') : 'Keine Werte aufgezeichnet'}`
                    )
                    .join('\n')}
    
                WICHTIGER BEOBACHTUNGSZEITRAUM-STATUS: ${observationPeriodStatus}
    
                Leistungswerte reichen von 1 bis 10:
                - 10 zeigt die höchste Produktivität und beste Leistung an.
                - 1 zeigt die niedrigste Produktivität und schlechteste Leistung an.
    
                WICHTIG: Beginnen Sie direkt mit der Analyse. Keine Einleitung wie "Okay, lass uns analysieren" oder "Hier ist meine Analyse". Keine Begrüßung oder abschließende Bemerkungen.
                
                Verwenden Sie nicht "Nutzer" in der Antwort, verwenden Sie stattdessen "Sie". Verwenden Sie nicht "vermuten"; wenn ein Wert 0 enthält, ist dieses Feld ohne Nutzereingabe leer.
                Es ist nicht notwendig, alle Einzelwerte aufzulisten, konzentrieren Sie sich auf Muster oder signifikante Werte.
                Schreiben Sie in aufeinanderfolgenden Sätzen und Absätzen, keine Aufzählungspunkte. Verwenden Sie nicht "Ich".
                
                ${stillInObservationPeriod ? "Erkennen Sie zu Beginn Ihrer Antwort an, dass der Beobachtungszeitraum noch läuft und dass vollständigere Daten eine genauere Analyse ermöglichen würden. Fahren Sie dann mit Ihrer Analyse basierend auf den verfügbaren Daten fort." : ""}
    
                Geben Sie Einblicke in die Leistungsmuster über die Woche und schlagen Sie Verbesserungen basierend auf der Verteilung der Werte vor. Stimmen Sie die Empfehlungen mit dem Chronotyp und dem empfohlenen Zeitplan ab. Stellen Sie sicher, dass die Antwort unter 500 Token bleibt und beenden Sie die Sätze, halten Sie die Antwort kompakt.
                WICHTIG: Antworten Sie auf Deutsch.
            ` : `
                Analyze the following performance data in conjunction with the user's chronotype "${chronotype}", 
                recommended wake-up time "${recommendedWakeUpTime}", and recommended sleep time "${recommendedSleepTime}":
    
                Here are the performance scores for each weekday:
                ${scoresByWeekday
                .map(
                    ({ weekday, scores }) =>
                        `${weekday}: ${scores.length > 0 ? scores.join(', ') : 'No scores recorded'}`
                )
                .join('\n')}
    
                IMPORTANT OBSERVATION PERIOD STATUS: ${observationPeriodStatus}
    
                Performance scores range from 1 to 10:
                - 10 indicates the highest productivity and best performance.
                - 1 indicates the lowest productivity and worst performance.
    
                IMPORTANT: Start directly with the analysis. No introduction like "Okay, let's analyze" or "Here is my analysis". No greetings or closing remarks.
                
                Do not use "user" in response, use "You" instead. Do not use "suppose"; if a value contains 0, that field is empty without the user's input. 
                It is not necessary to list all the single values, just focus on pattern or significant values. 
                write in consecutive sentences and paragraphs, no bullet points. Do not use "I".
                
                ${stillInObservationPeriod ? "At the beginning of your response, acknowledge that the observation period is still ongoing and that more complete data would provide a more accurate analysis. Then proceed with your analysis based on the available data." : ""}
    
                Provide insights into the performance patterns across the week and suggest improvements based on the distribution of scores. Align the recommendations with the user's chronotype and recommended schedule. Ensure the response is under 500 tokens and finish the sentences, keep response compact.
            `;

            // Process one request at a time to avoid overwhelming the API
            const failedSections = [];
            const sectionLabels = {
                performanceLineChart: { en: 'Daily Performance Curve', de: 'Tägliche Leistungskurve' },
                wakeBedQuality: { en: 'Sleep Quality Analysis', de: 'Schlafqualitätsanalyse' },
                totalScoresTimeSlot: { en: 'Performance by Time Slot', de: 'Leistung nach Zeitfenster' },
            };
            const trackFailure = (key, error) => {
                console.error(`Failed to get ${key} analysis:`, error);
                failedSections.push({ key, status: error?.status });
            };

            let performanceLineChartAnalysis;
            try {
                performanceLineChartAnalysis = await makeGeminiRequest(performanceLineChartPrompt);
            } catch (error) {
                trackFailure('performanceLineChart', error);
                performanceLineChartAnalysis = "Analysis could not be generated. Please try again later.";
            }

            let wakeBedQualityAnalysis;
            try {
                wakeBedQualityAnalysis = await makeGeminiRequest(wakeBedQualityPrompt);
            } catch (error) {
                trackFailure('wakeBedQuality', error);
                wakeBedQualityAnalysis = "Analysis could not be generated. Please try again later.";
            }

            let totalScoresTimeSlotAnalysis;
            try {
                totalScoresTimeSlotAnalysis = await makeGeminiRequest(totalScoresTimeSlotPrompt);
            } catch (error) {
                trackFailure('totalScoresTimeSlot', error);
                totalScoresTimeSlotAnalysis = "Analysis could not be generated. Please try again later.";
            }

            if (failedSections.length > 0) {
                const hadOverload = failedSections.some(f => f.status === 503);
                const hadRateLimit = failedSections.some(f => f.status === 429);
                const failedNames = failedSections
                    .map(f => sectionLabels[f.key][isGerman ? 'de' : 'en'])
                    .join(isGerman ? ', ' : ', ');
                let reason;
                if (isGerman) {
                    reason = hadOverload
                        ? 'Der Analysedienst ist derzeit überlastet. Bitte versuchen Sie es in einigen Minuten erneut.'
                        : hadRateLimit
                            ? 'Das Anfragelimit des Analysedienstes wurde erreicht. Bitte später erneut versuchen.'
                            : 'Bitte erneut versuchen.';
                    alert(`${failedSections.length} von 3 Analysen konnten nicht erstellt werden: ${failedNames}. ${reason} Sie können den Bericht trotzdem mit den verfügbaren Abschnitten herunterladen.`);
                } else {
                    reason = hadOverload
                        ? 'The analysis service is currently overloaded. Please try again in a few minutes.'
                        : hadRateLimit
                            ? 'The analysis service request limit was reached. Please try again later.'
                            : 'Please try again.';
                    alert(`${failedSections.length} of 3 analyses could not be generated: ${failedNames}. ${reason} You can still download the report with the available sections.`);
                }
            }

            setChartData({
                performanceLineChart,
                totalScoresChart,
                scoresByTimeSlotChart,
                analysis: performanceLineChartAnalysis,
                wakeBedQualityAnalysis,
                totalScoresTimeSlotAnalysis,
                dailySurveyData: filteredDailySurveyResponses,
                performanceData: filteredPerformanceResponses
            });

            setIsLoading(false);
            if (failedSections.length === 0) {
                alert(isGerman ? 'Vorschau abgeschlossen. Sie können Ihren Bericht jetzt herunterladen.' : 'Preview completed. You can now download your report.');
            }
        } catch (error) {
            console.error('Error generating analysis:', error);
            setIsLoading(false);
            alert('Failed to generate analysis. Please try again.');
        }
    };

    const handleDownload = async () => {
        if (
            !chartData ||
            !chartData.analysis ||
            !chartData.wakeBedQualityAnalysis ||
            !chartData.totalScoresTimeSlotAnalysis
        ) {
            alert(t('alerts.previewData'));
            return;
        }

        const pdf = new jsPDF();
        const pageHeight = pdf.internal.pageSize.height;
        const pageWidth = pdf.internal.pageSize.width;
        let currentPage = 1;

        // Check language for PDF generation
        const isGerman = i18n.language === 'de';

        // Define modern color scheme
        const colors = {
            primary: [25, 118, 210],      // Blue
            secondary: [76, 175, 80],      // Green
            accent: [255, 152, 0],         // Orange
            dark: [33, 33, 33],            // Dark gray
            light: [245, 245, 245],        // Light gray
            white: [255, 255, 255]
        };

        // Helper function to add page header
        const addPageHeader = () => {
            // Add gradient-like header background
            pdf.setFillColor(...colors.primary);
            pdf.rect(0, 0, pageWidth, 25, 'F');

            // Add UFLOW branding
            pdf.setTextColor(...colors.white);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(16);
            pdf.text('UFLOW', 15, 15);

            // Add tagline
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.text('Individual Flow Analysis Report', 15, 20);

            // Add date on the right
            pdf.setFontSize(9);
            const date = new Date().toLocaleDateString(isGerman ? 'de-DE' : 'en-US');
            pdf.text(date, pageWidth - 15, 20, { align: 'right' });

            pdf.setTextColor(...colors.dark);
        };

        // Helper function to add page footer
        const addPageFooter = () => {
            pdf.setFillColor(...colors.light);
            pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');

            pdf.setTextColor(100, 100, 100);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.text(`${isGerman ? 'Seite' : 'Page'} ${currentPage}`, pageWidth / 2, pageHeight - 8, { align: 'center' });


            pdf.setTextColor(...colors.dark);
        };

        // Helper function to add modern section divider
        const addSectionDivider = (title, offsetY) => {
            // Add colored section header
            pdf.setFillColor(...colors.primary);
            pdf.rect(0, offsetY - 5, pageWidth, 15, 'F');

            pdf.setTextColor(...colors.white);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(14);
            pdf.text(title, 15, offsetY + 3);

            pdf.setTextColor(...colors.dark);
            return offsetY + 20;
        };

        // Helper function to add styled text with better formatting
        const addStyledText = (text, x, y, options = {}) => {
            const {
                fontSize = 10,
                fontStyle = 'normal',
                color = colors.dark,
                maxWidth = pageWidth - 30,
                lineHeight = 1.5,
                align = 'left',
                indent = 0
            } = options;

            pdf.setFontSize(fontSize);
            pdf.setFont('helvetica', fontStyle);
            pdf.setTextColor(...color);

            const lines = pdf.splitTextToSize(text, maxWidth);
            let currentY = y;

            lines.forEach((line) => {
                if (currentY + (fontSize * lineHeight / 2) > pageHeight - 20) {
                    pdf.addPage();
                    currentPage++;
                    addPageHeader();
                    addPageFooter();
                    currentY = 35;
                    // Reset font settings after adding header/footer
                    pdf.setFontSize(fontSize);
                    pdf.setFont('helvetica', fontStyle);
                    pdf.setTextColor(...color);
                }

                const textX = align === 'center' ? pageWidth / 2 : (align === 'right' ? pageWidth - 15 : x + indent);
                pdf.text(line, textX, currentY, { align });
                currentY += fontSize * lineHeight / 2;
            });

            return currentY;
        };

        // Helper function to draw line chart directly in PDF
        const drawLineChart = (data, y, title, color, yAxisLabel, maxY = null, isTimeChart = false) => {
            const chartWidth = 170;
            const chartHeight = 60;
            const x = (pageWidth - chartWidth) / 2;
            const padding = { top: 10, right: 10, bottom: 20, left: 25 };

            // Check if we need a new page
            if (y + chartHeight + 40 > pageHeight - 20) {
                pdf.addPage();
                currentPage++;
                addPageHeader();
                addPageFooter();
                y = 35;
            }

            // Add title
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(...colors.dark);
            pdf.text(title, pageWidth / 2, y, { align: 'center' });
            y += 8;

            // Draw chart background
            pdf.setFillColor(...colors.white);
            pdf.rect(x, y, chartWidth, chartHeight, 'F');

            // Draw border
            pdf.setDrawColor(...colors.primary);
            pdf.setLineWidth(0.5);
            pdf.rect(x, y, chartWidth, chartHeight, 'S');

            if (!data || data.length === 0) {
                pdf.setFontSize(10);
                pdf.setTextColor(150, 150, 150);
                pdf.text(isGerman ? 'Keine Daten angegeben' : 'No data provided',
                    x + chartWidth / 2, y + chartHeight / 2, { align: 'center' });
                return y + chartHeight + 15;
            }

            const plotX = x + padding.left;
            const plotY = y + padding.top;
            const plotWidth = chartWidth - padding.left - padding.right;
            const plotHeight = chartHeight - padding.top - padding.bottom;

            // Calculate scales - use actual data range for time charts
            const dataPoints = data.map(d => d.value);
            const minValue = Math.min(...dataPoints);
            const maxValue = isTimeChart ? Math.max(...dataPoints) : (maxY || Math.max(...dataPoints));
            const valueRange = maxValue - minValue || 1;

            // Draw Y-axis labels
            pdf.setFontSize(7);
            pdf.setTextColor(100, 100, 100);
            const ySteps = 4;
            for (let i = 0; i <= ySteps; i++) {
                const value = minValue + (valueRange * i / ySteps);
                const labelY = plotY + plotHeight - (plotHeight * i / ySteps);
                let labelText;
                if (isTimeChart) {
                    let hours = Math.floor(value);
                    const minutes = Math.round((value - hours) * 60);

                    // Handle after-midnight times (24+ hours)
                    if (hours >= 24) {
                        hours = hours - 24;
                        labelText = `${hours}:${minutes.toString().padStart(2, '0')} +1`;
                    } else {
                        labelText = `${hours}:${minutes.toString().padStart(2, '0')}`;
                    }
                } else {
                    labelText = value.toFixed(0);
                }
                pdf.text(labelText, plotX - 5, labelY + 2, { align: 'right' });
            }

            // Draw data points and lines
            pdf.setDrawColor(...color);
            pdf.setFillColor(...color);
            pdf.setLineWidth(1);

            for (let i = 0; i < data.length; i++) {
                const pointX = plotX + (plotWidth * i / Math.max(data.length - 1, 1));
                const normalizedValue = (data[i].value - minValue) / valueRange;
                const pointY = plotY + plotHeight - (plotHeight * normalizedValue);

                // Draw line to next point
                if (i < data.length - 1) {
                    const nextX = plotX + (plotWidth * (i + 1) / Math.max(data.length - 1, 1));
                    const nextNormalizedValue = (data[i + 1].value - minValue) / valueRange;
                    const nextY = plotY + plotHeight - (plotHeight * nextNormalizedValue);
                    pdf.line(pointX, pointY, nextX, nextY);
                }

                // Draw point
                pdf.circle(pointX, pointY, 1.5, 'F');
            }

            // Draw X-axis labels (show first, middle, last dates)
            pdf.setFontSize(6);
            if (data.length > 0) {
                const indicesToShow = data.length <= 3
                    ? data.map((_, i) => i)
                    : [0, Math.floor(data.length / 2), data.length - 1];

                indicesToShow.forEach(i => {
                    const pointX = plotX + (plotWidth * i / Math.max(data.length - 1, 1));
                    const label = data[i].label;
                    pdf.text(label, pointX, plotY + plotHeight + 8, {
                        align: 'center',
                        maxWidth: 30
                    });
                });
            }

            return y + chartHeight + 15;
        };

        // Helper function to add charts with modern frame
        const addChartWithFrame = async (selector, y, title) => {
            const element = document.querySelector(selector);
            if (element) {
                try {
                    const canvas = await html2canvas(element, {
                        scale: 2,
                        logging: false,
                        backgroundColor: '#ffffff'
                    });
                    const imgData = canvas.toDataURL('image/png');

                    const imgWidth = 170;
                    const imgHeight = 70;
                    const x = (pageWidth - imgWidth) / 2;

                    // Check if we need a new page
                    if (y + imgHeight + 30 > pageHeight - 20) {
                        pdf.addPage();
                        currentPage++;
                        addPageHeader();
                        addPageFooter();
                        y = 35;
                    }

                    // Add shadow effect
                    pdf.setFillColor(200, 200, 200);
                    pdf.rect(x + 2, y + 2, imgWidth, imgHeight, 'F');

                    // Add white background
                    pdf.setFillColor(...colors.white);
                    pdf.rect(x, y, imgWidth, imgHeight, 'F');

                    // Add chart
                    pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

                    // Add frame
                    pdf.setDrawColor(...colors.primary);
                    pdf.setLineWidth(0.5);
                    pdf.rect(x, y, imgWidth, imgHeight, 'S');

                    // Add title if provided
                    if (title) {
                        pdf.setFontSize(9);
                        pdf.setFont('helvetica', 'italic');
                        pdf.setTextColor(100, 100, 100);
                        pdf.text(title, pageWidth / 2, y + imgHeight + 8, { align: 'center' });
                        return y + imgHeight + 15;
                    }

                    return y + imgHeight + 10;
                } catch (error) {
                    console.error('Error capturing chart:', error);
                    return y;
                }
            }
            return y;
        };

        // Helper function to parse markdown bold and render text with mixed formatting
        const addFormattedText = (text, x, y, options = {}) => {
            const {
                fontSize = 10,
                color = colors.dark,
                maxWidth = pageWidth - 30,
                lineHeight = 1.6,
                indent = 0
            } = options;

            pdf.setFontSize(fontSize);
            pdf.setTextColor(...color);

            // Parse text for **bold** markdown
            const segments = [];
            const regex = /(\*\*.*?\*\*)/g;
            const parts = text.split(regex);

            parts.forEach(part => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    // Bold text
                    segments.push({ text: part.slice(2, -2), bold: true });
                } else if (part) {
                    // Normal text
                    segments.push({ text: part, bold: false });
                }
            });

            // Render segments
            let currentY = y;
            let currentLine = '';
            let currentSegments = [];

            const flushLine = () => {
                if (currentSegments.length === 0) return;

                // Check if we need a new page
                if (currentY + (fontSize * lineHeight / 2) > pageHeight - 20) {
                    pdf.addPage();
                    currentPage++;
                    addPageHeader();
                    addPageFooter();
                    currentY = 35;
                    pdf.setFontSize(fontSize);
                    pdf.setTextColor(...color);
                }

                // Render the line with mixed formatting
                let currentX = x + indent;
                currentSegments.forEach(seg => {
                    pdf.setFont('helvetica', seg.bold ? 'bold' : 'normal');
                    pdf.text(seg.text, currentX, currentY);
                    currentX += pdf.getTextWidth(seg.text);
                });

                currentY += fontSize * lineHeight / 2;
                currentLine = '';
                currentSegments = [];
            };

            segments.forEach(segment => {
                const words = segment.text.split(' ');

                words.forEach((word, idx) => {
                    const testText = currentLine + (currentLine ? ' ' : '') + word;
                    pdf.setFont('helvetica', segment.bold ? 'bold' : 'normal');
                    const testWidth = pdf.getTextWidth(testText);

                    if (testWidth > maxWidth - indent) {
                        // Line is too long, flush current line
                        flushLine();
                        currentLine = word;
                        currentSegments = [{ text: word + (idx < words.length - 1 ? ' ' : ''), bold: segment.bold }];
                    } else {
                        currentLine = testText;
                        const addSpace = idx < words.length - 1 || segment !== segments[segments.length - 1];
                        currentSegments.push({ text: word + (addSpace ? ' ' : ''), bold: segment.bold });
                    }
                });
            });

            // Flush remaining line
            if (currentSegments.length > 0) {
                flushLine();
            }

            return currentY;
        };

        // Helper function to add analysis text with modern styling and bold support
        const addAnalysisSection = (text, startY) => {
            let y = startY;
            const paragraphs = text.split('\n').filter(p => p.trim());

            paragraphs.forEach((paragraph, index) => {
                if (index > 0) y += 3; // Add space between paragraphs

                // Check for bullet points
                if (paragraph.trim().startsWith('•') || paragraph.trim().startsWith('-')) {
                    // Add bullet point
                    pdf.setFillColor(...colors.accent);
                    pdf.circle(17, y - 2, 1, 'F');
                    y = addFormattedText(paragraph.replace(/^[•-]\s*/, ''), 15, y, {
                        indent: 10,
                        fontSize: 10,
                        lineHeight: 1.6
                    });
                } else {
                    y = addFormattedText(paragraph, 15, y, {
                        fontSize: 10,
                        lineHeight: 1.6
                    });
                }
            });

            return y;
        };

        // Start creating the PDF
        addPageHeader();
        addPageFooter();

        // Add cover page elements
        let offsetY = 45;

        // Add main title with modern styling
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(24);
        pdf.setTextColor(...colors.primary);
        pdf.text(
            'Your Individual',
            pageWidth / 2,
            offsetY,
            { align: 'center' }
        );

        offsetY += 12;
        pdf.setFontSize(28);
        pdf.text(
            'Flow Analysis',
            pageWidth / 2,
            offsetY,
            { align: 'center' }
        );

        // Add decorative line
        offsetY += 15;
        pdf.setDrawColor(...colors.accent);
        pdf.setLineWidth(2);
        pdf.line(pageWidth / 2 - 40, offsetY, pageWidth / 2 + 40, offsetY);

        // Add user info box
        offsetY += 25;
        pdf.setFillColor(...colors.light);
        pdf.roundedRect(30, offsetY, pageWidth - 60, 50, 5, 5, 'F');

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(...colors.dark);

        offsetY += 15;
        if (userFirstName || userLastName) {
            pdf.text(`${isGerman ? 'Name' : 'Name'}: ${userFirstName} ${userLastName}`, 40, offsetY);
            offsetY += 10;
        }
        pdf.text(`${isGerman ? 'Chronotyp' : 'Chronotype'}: ${chronotype}`, 40, offsetY);
        offsetY += 10;
        pdf.text(`${isGerman ? 'Empfohlene Aufwachzeit' : 'Recommended Wake Time'}: ${recommendedWakeUpTime}`, 40, offsetY);
        offsetY += 10;
        pdf.text(`${isGerman ? 'Empfohlene Schlafenszeit' : 'Recommended Sleep Time'}: ${recommendedSleepTime}`, 40, offsetY);

        // Add observation period info
        offsetY += 25;
        if (selectedObservationPeriod) {
            const startDate = new Date(selectedObservationPeriod.startDate).toLocaleDateString(isGerman ? 'de-DE' : 'en-US');
            const endDate = new Date(selectedObservationPeriod.endDate).toLocaleDateString(isGerman ? 'de-DE' : 'en-US');

            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(9);
            pdf.setTextColor(100, 100, 100);
            pdf.text(
                `${isGerman ? 'Beobachtungszeitraum' : 'Observation Period'}: ${startDate} - ${endDate}`,
                pageWidth / 2,
                offsetY,
                { align: 'center' }
            );
        }

        // Section 1: Performance Analysis
        pdf.addPage();
        currentPage++;
        addPageHeader();
        addPageFooter();

        offsetY = addSectionDivider(
            isGerman ? 'Leistungsanalyse' : 'Performance Analysis',
            35
        );

        offsetY = await addChartWithFrame('#performance-line-chart', offsetY,
            isGerman ? 'Tägliche Leistungskurve' : 'Daily Performance Curve');

        offsetY += 10;
        offsetY = addAnalysisSection(chartData.analysis, offsetY);

        // Section 2: Sleep Quality Analysis
        pdf.addPage();
        currentPage++;
        addPageHeader();
        addPageFooter();

        offsetY = addSectionDivider(
            isGerman ? 'Schlafqualitätsanalyse' : 'Sleep Quality Analysis',
            35
        );

        // Prepare data for daily survey charts
        const parseTime = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            return hours + minutes / 60;
        };

        // Parse bed time with adjustment for after-midnight times
        // Times from 0:00 to 6:00 are treated as 24:00 to 30:00 for better visualization
        const parseBedTime = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            let adjustedHours = hours;

            // If bedtime is between midnight and 6 AM, treat it as next day (24+ hours)
            if (hours >= 0 && hours < 6) {
                adjustedHours = hours + 24;
            }

            return adjustedHours + minutes / 60;
        };

        const dailySurveyData = chartData.dailySurveyData || [];

        const wakeUpTimeData = dailySurveyData.map(response => ({
            label: new Date(response.date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US', {
                month: 'numeric',
                day: 'numeric'
            }),
            value: parseTime(response.wakeUpTime)
        }));

        const bedTimeData = dailySurveyData.map(response => ({
            label: new Date(response.date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US', {
                month: 'numeric',
                day: 'numeric'
            }),
            value: parseBedTime(response.bedTime)
        }));

        // Filter out responses that don't have sleep quality data
        const sleepQualityData = dailySurveyData
            .filter(response => response.sleepQuality !== undefined && response.sleepQuality !== null)
            .map(response => ({
                label: new Date(response.date).toLocaleDateString(isGerman ? 'de-DE' : 'en-US', {
                    month: 'numeric',
                    day: 'numeric'
                }),
                value: response.sleepQuality
            }));

        // Draw the charts directly
        offsetY = drawLineChart(
            wakeUpTimeData,
            offsetY,
            isGerman ? 'Aufwachzeiten' : 'Wake-up Times',
            [136, 132, 216], // Purple color
            'Hours',
            null, // Use dynamic range based on actual data
            true // isTimeChart
        );

        offsetY = drawLineChart(
            bedTimeData,
            offsetY,
            isGerman ? 'Schlafenszeiten' : 'Bedtimes',
            [130, 202, 157], // Green color
            'Hours',
            null, // Use dynamic range based on actual data
            true // isTimeChart
        );

        offsetY = drawLineChart(
            sleepQualityData,
            offsetY,
            isGerman ? 'Schlafqualität' : 'Sleep Quality',
            [255, 115, 0], // Orange color
            'Quality',
            10,
            false // not a time chart
        );

        offsetY += 10;
        offsetY = addAnalysisSection(chartData.wakeBedQualityAnalysis, offsetY);

        // Section 3: Weekly Performance Patterns
        pdf.addPage();
        currentPage++;
        addPageHeader();
        addPageFooter();

        offsetY = addSectionDivider(
            isGerman ? 'Wöchentliche Leistungsmuster' : 'Weekly Performance Patterns',
            35
        );

        offsetY = await addChartWithFrame('#total-scores-chart-container', offsetY,
            isGerman ? 'Gesamtwerte pro Wochentag' : 'Total Scores by Weekday');
        offsetY = await addChartWithFrame('#time-slot-chart-container', offsetY,
            isGerman ? 'Leistung nach Zeitfenster' : 'Performance by Time Slot');

        offsetY += 10;
        offsetY = addAnalysisSection(chartData.totalScoresTimeSlotAnalysis, offsetY);

        // Add disclaimer on the last page
        if (offsetY + 50 > pageHeight - 20) {
            pdf.addPage();
            currentPage++;
            addPageHeader();
            addPageFooter();
            offsetY = 35;
        }

        offsetY = pageHeight - 40;
        pdf.setDrawColor(200, 200, 200);
        pdf.line(30, offsetY, pageWidth - 30, offsetY);

        offsetY += 10;
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        const disclaimer = isGerman
            ? 'Dieser Bericht dient nur zu Informationszwecken und ersetzt keine professionelle medizinische Beratung.'
            : 'This report is for informational purposes only and does not replace professional medical advice.';
        pdf.text(disclaimer, pageWidth / 2, offsetY, { align: 'center' });

        // Save the PDF with a professional filename
        const fileName = isGerman
            ? `UFLOW_Gesundheitsanalyse_${new Date().toISOString().split('T')[0]}.pdf`
            : `UFLOW_Health_Analysis_${new Date().toISOString().split('T')[0]}.pdf`;

        pdf.save(fileName);
    };

    return (
        <div>
            <Tooltip title={t('overview.downloadReport')}>
                <IconButton onClick={() => setOpenDialog(true)} data-tour="download-report-button">
                    <DownloadIcon />
                </IconButton>
            </Tooltip>
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {isAdmin ?
                        `${t('overview.selectObservationPeriod')} (Admin View)` :
                        t('overview.selectObservationPeriod')}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{t('overview.selectPeriod')}</InputLabel>
                        <Select
                            value={selectedObservationPeriod || ''}
                            onChange={(e) => setSelectedObservationPeriod(e.target.value)}
                        >
                            {observationPeriods.map((period, index) => (
                                <MenuItem key={index} value={period}>
                                    {t('overview.observationPeriod', {
                                        index: index + 1,
                                        start: new Date(period.startDate).toLocaleDateString(),
                                        end: new Date(period.endDate).toLocaleDateString()
                                    })}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {chartData && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                {t('overview.preview')}
                            </Typography>

                            <Box sx={{ height: 300, mb: 3 }} id="performance-line-chart">
                                <Line data={chartData.performanceLineChart} options={chartOptions} />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <Box sx={{ flex: 1, height: 200 }} id="total-scores-chart-container">
                                    {chartData.totalScoresChart && (
                                        <Bar data={chartData.totalScoresChart} options={{
                                            ...chartOptions,
                                            plugins: {
                                                ...chartOptions.plugins,
                                                title: {
                                                    display: true,
                                                    text: t('overview.aggregateScoresChart')
                                                }
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    max: 10
                                                }
                                            }
                                        }} />
                                    )}
                                </Box>
                                <Box sx={{ flex: 1, height: 200 }} id="time-slot-chart-container">
                                    {chartData.scoresByTimeSlotChart && (
                                        <Line data={chartData.scoresByTimeSlotChart} options={{
                                            ...chartOptions,
                                            plugins: {
                                                ...chartOptions.plugins,
                                                title: {
                                                    display: true,
                                                    text: t('overview.scoresByTimeSlotChart')
                                                }
                                            }
                                        }} />
                                    )}
                                </Box>
                            </Box>

                            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                {t('overview.dailySurveyCharts')}
                            </Typography>
                            <DailySurveyChart
                                ref={surveyChartRefs}
                                responses={chartData.dailySurveyData || []}
                                noData={!chartData.dailySurveyData || chartData.dailySurveyData.length === 0}
                                userId={userId}
                                idPrefix="pdf-preview-"
                                isAdmin={isAdmin}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>{t('overview.cancel')}</Button>
                    <Button
                        onClick={handlePreview}
                        disabled={!selectedObservationPeriod || isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} /> : t('overview.preview')}
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={!chartData}
                        variant="contained"
                    >
                        {t('overview.download')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PreviewAndDownload;